import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { Button } from '@connected/ui/components/button';
import { Popout } from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormTouched } from '@/hooks/use-form-touched';
import { showConfirm } from '@/stores/confirmer';

import type { TechnicalDrawingFormFields } from '../utils/drawing-types';

import { useCreateDrawing } from '../hooks/use-drawing-mutations';
import { drawingDefaultValues } from '../utils/drawing-defaults';
import { DrawingFormFields } from './drawing-form-fields';

export type DrawingCreateModalProps = {
    electricalProjectVersionId: string;
    trigger: React.ReactNode;
};

export function DrawingCreateModal({ electricalProjectVersionId, trigger }: DrawingCreateModalProps) {
    const { t } = useTranslation('applications');
    const [open, setOpen] = useState(false);

    const { mutation: createDrawingMutation, disabled } = useCreateDrawing({ electricalProjectVersionId });

    const form = useForm<TechnicalDrawingFormFields, ZodValidator>({
        defaultValues: drawingDefaultValues(),
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            const body = {
                ElectricalProjectVersionId: electricalProjectVersionId,
                ElectricalProjectTechnicalDrawingId: crypto.randomUUID(),
                ElectricalProjectFileId: value.ElectricalProjectFileId ?? null,
                Name: value.Name ?? null!,
                Description: value.Description ?? null,
                Location: value.Location ?? null,
                IsDrawingPresent: value.IsDrawingPresent ?? null,
                Order: null,
            };

            const result = await createDrawingMutation.mutateAsync({ body });

            if (result.Result !== 'Success')
                return;

            form.reset();
            setOpen(false);
        },
    });

    const isTouched = useFormTouched(form.store, drawingDefaultValues);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
    }

    async function handleOpenChange(open: boolean) {
        if (open) {
            setOpen(true);
            return;
        }
        else if (!isTouched) {
            setOpen(false);
            return;
        }

        if (await showConfirm(t('messages.general.confirm_cancel', { ns: 'common' }))) {
            form.reset();
            setOpen(false);
        }
    }

    return (
        <>
            <Popout open={open} onOpenChange={handleOpenChange}>
                {trigger && <Popout.Trigger asChild>{trigger}</Popout.Trigger>}
                <Popout.Content asChild>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                        <Popout.Header>
                            <Popout.Title>Technische tekening toevoegen</Popout.Title>
                            <Popout.Description className="sr-only">
                                Een korte omschrijving van de tekening en eventuele bijzonderheden.
                            </Popout.Description>
                        </Popout.Header>
                        <Popout.Body>
                            <DrawingFormFields
                                form={form}
                                disabled={disabled}
                                electricalProjectVersionId={electricalProjectVersionId}
                            />
                        </Popout.Body>
                        <Popout.Footer>
                            <Popout.Close asChild>
                                <Button variant="outline" className="w-full" disabled={disabled}>
                                    {t('actions.general.cancel', { ns: 'common' })}
                                </Button>
                            </Popout.Close>
                            <Button type="submit" className="w-full" disabled={disabled}>
                                {t('actions.selection.add', { ns: 'common' })}
                            </Button>
                        </Popout.Footer>
                    </form>
                </Popout.Content>
            </Popout>
            {createDrawingMutation.isError && <em className="text-red-500">{createDrawingMutation.error.Message}</em>}
        </>
    );
}
